import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { UnsavedChangesGuard } from 'components/UnsavedChangesGuard';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import { REVIEW_ORDER_EDITING_REQUEST } from 'gql/mutations';
import { canManageRequests, MerchantRequest } from 'types/resolution';
import { isShipaidAdmin } from 'utils';
import { ReviewQueue } from './ExpandedWorkspace';

export function OrderEditingRequestReview({
  request,
  onReviewed,
  onClose,
  disabled = false,
}: {
  request: MerchantRequest;
  onReviewed: () => void;
  onClose: () => void;
  disabled?: boolean;
}) {
  const { storeId } = useStore();
  const { role, user } = useUser();
  const allowed = canManageRequests(role, isShipaidAdmin(user));
  const scope = `${storeId}:${request.id}`;
  const selectedScope = useRef(allowed ? scope : '');
  const pending = useRef<string>();
  const [review] = useMutation(REVIEW_ORDER_EDITING_REQUEST);
  const [state, setState] = useState({
    scope,
    dirty: false,
    busy: false,
    done: false,
    error: '',
    notice: '',
  });
  const current = state.scope === scope ? state : undefined;
  const details =
    request.type === 'ORDER_EDITING' &&
    request.orderEditingRequest?.id === request.id
      ? request.orderEditingRequest
      : undefined;
  const onDirtyChange = useCallback(
    (dirty: boolean) => {
      setState((previous) =>
        previous.scope === scope
          ? { ...previous, dirty }
          : { scope, dirty, busy: false, done: false, error: '', notice: '' },
      );
    },
    [scope],
  );
  useLayoutEffect(() => {
    selectedScope.current = allowed ? scope : '';
    return () => {
      selectedScope.current = '';
    };
  }, [allowed, scope]);

  if (!allowed || !storeId)
    return (
      <p>
        An Admin or Manager role is required to review Order Editing requests.
      </p>
    );

  const onReview = async (
    requestId: string,
    decision: 'approve' | 'reject',
    note: string,
    fulfillmentPaused?: boolean,
  ) => {
    if (
      disabled ||
      !details ||
      requestId !== request.id ||
      selectedScope.current !== scope ||
      pending.current === scope ||
      !['open', 'awaiting_approval'].includes(details.status) ||
      (decision === 'approve' &&
        (details.action === 'shipping' ||
          (details.requiresFulfillmentPause && fulfillmentPaused !== true)))
    )
      return;
    if (note.length > 500) {
      setState((previous) => ({
        ...previous,
        scope,
        error: 'Review notes must be 500 characters or fewer.',
      }));
      return;
    }
    pending.current = scope;
    setState((previous) => ({ ...previous, scope, busy: true, error: '' }));
    try {
      await review({
        variables: {
          input: {
            storeId,
            requestId,
            decision,
            note,
            ...(fulfillmentPaused !== undefined ? { fulfillmentPaused } : {}),
          },
        },
      });
    } catch (_) {
      if (selectedScope.current === scope)
        setState((previous) => ({
          ...previous,
          busy: false,
          error:
            'Unable to review this request. Refresh its status before trying again.',
        }));
      if (pending.current === scope) pending.current = undefined;
      return;
    }
    if (pending.current === scope) pending.current = undefined;
    if (selectedScope.current !== scope) return;
    setState({
      scope,
      dirty: false,
      busy: false,
      done: true,
      error: '',
      notice:
        decision === 'reject'
          ? 'Request rejected.'
          : details.action === 'support'
          ? 'Support request resolved.'
          : 'Proposal approved. The customer still needs to confirm the change while eligible.',
    });
    onReviewed();
  };

  return (
    <section
      aria-label={`Order Editing request ${request.number}`}
      className="space-y-4 rounded-xl border border-blue-200 bg-blue-50 p-4 sm:p-5"
    >
      <UnsavedChangesGuard
        hasUnsavedChanges={Boolean(current?.dirty && !current.done)}
        onSave={async () => false}
        saveBeforeLeaving={false}
        message="Your review notes or fulfillment confirmations have not been submitted. Continue reviewing or leave without keeping this draft."
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold text-gray-900">
          Order Editing request {request.number}
        </h3>
        <button
          type="button"
          aria-label="Close Order Editing request"
          onClick={onClose}
          className="rounded px-2 py-1 text-sm font-medium text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Close review
        </button>
      </div>
      {current?.error && (
        <p
          role="alert"
          className="rounded-lg bg-red-50 p-3 text-sm text-red-800"
        >
          {current.error}
        </p>
      )}
      {current?.notice && (
        <p
          role="status"
          className="rounded-lg bg-green-50 p-3 text-sm text-green-800"
        >
          {current.notice}
        </p>
      )}
      {disabled && (
        <p className="text-sm text-amber-900">
          Refresh the list to confirm this request before reviewing.
        </p>
      )}
      {!details ? (
        <p role="alert" className="text-sm text-red-800">
          Request details are unavailable. Refresh the list before reviewing.
        </p>
      ) : current?.done ? null : ['open', 'awaiting_approval'].includes(
          details.status,
        ) ? (
        <ReviewQueue
          key={scope}
          requests={[details]}
          busy={Boolean(current?.busy) || disabled}
          reviewing={current?.busy ? request.id : undefined}
          onReview={onReview}
          onDirtyChange={onDirtyChange}
        />
      ) : (
        <div className="rounded-lg bg-white p-4 text-sm text-gray-700">
          <p>{details.summary}</p>
          <p className="mt-2">{request.statusLabel}</p>
          <p className="mt-2">This request no longer needs merchant review.</p>
        </div>
      )}
    </section>
  );
}
